import i18n from 'i18n'
import { ValidationSchema } from '@/hooks/form/models'
import {
  REGEX_NUMBERS,
  REGEX_TEXTAREA,
  REGEX_CONSECUTIVE_WHITESPACE,
  REGEX_NUMBER_FLOAT,
} from '@/constants/regex'
import {
  MAX_BONUS_PRIORITY,
  MAX_FREE_SPIN_GAMES,
  MAX_FREE_SPIN_QUANTITY,
  MIN_BONUS_PRIORITY,
  MIN_FREE_SPIN_QUANTITY,
} from '@/constants'

import { setRegexLength, currencyFormat } from '@/utils'
import {
  BonusPeriodRecurrenceEnum,
  BonusModeType,
  BonusWageringOrderType,
  BonusType,
  ExpirationType,
} from '@/enums'

export const bonusPeriodValidator: ValidationSchema = {
  recurrence: {
    required: false,
    validators: [],
  },
  startDate: {
    required: true,
    validators: [],
    dependencie: {
      fieldName: 'recurrence',
      conditional: (value: string) =>
        value === BonusPeriodRecurrenceEnum.dateRange,
    },
  },
  startTime: {
    required: true,
    validators: [],
    dependencie: {
      fieldName: 'recurrence',
      conditional: (value: string) =>
        value !== '' && value !== 'noPeriod' && value !== undefined,
    },
  },
  endDate: {
    required: true,
    validators: [],
    dependencie: {
      fieldName: 'recurrence',
      conditional: (value: string) =>
        value === BonusPeriodRecurrenceEnum.dateRange,
    },
  },
  endTime: {
    required: true,
    validators: [],
    dependencie: {
      fieldName: 'recurrence',
      conditional: (value: string) =>
        value !== '' && value !== 'noPeriod' && value !== undefined,
    },
  },
  recurrenceWeekday: {
    required: true,
    validators: [],
    dependencie: {
      fieldName: 'recurrence',
      conditional: (value: string) =>
        value === BonusPeriodRecurrenceEnum.weekday,
    },
  },
  day: {
    required: true,
    validators: [],
    dependencie: {
      fieldName: 'recurrence',
      conditional: (value: string) =>
        value === BonusPeriodRecurrenceEnum.weekday,
    },
  },
}

export const bonusDetailsValidator: ValidationSchema = {
  name: {
    required: true,
    validators: [
      {
        regExp: REGEX_TEXTAREA,
        error: i18n.t('regexValidators.REGEX_TEXTAREA'),
      },
      {
        regExpMatch: REGEX_CONSECUTIVE_WHITESPACE,
        error: i18n.t('error.consecutiveWhitespace'),
      },
      {
        regExp: setRegexLength(90),
        error: i18n.t('messages.inputMaxLength', {
          field: i18n.t('labels.bonusName'),
          max: '90',
        }),
      },
    ],
  },
  description: {
    required: true,
    validators: [
      {
        regExp: REGEX_TEXTAREA,
        error: i18n.t('regexValidators.REGEX_TEXTAREA'),
      },
      {
        regExpMatch: REGEX_CONSECUTIVE_WHITESPACE,
        error: i18n.t('error.consecutiveWhitespace'),
      },
      {
        regExp: setRegexLength(90),
        error: i18n.t('messages.inputMaxLength', {
          field: i18n.t('labels.bonusDescription'),
          max: '90',
        }),
      },
    ],
  },
  code: {
    required: true,
    validators: [
      {
        regExp: REGEX_TEXTAREA,
        error: i18n.t('regexValidators.REGEX_TEXTAREA'),
      },
      {
        regExpMatch: REGEX_CONSECUTIVE_WHITESPACE,
        error: i18n.t('error.consecutiveWhitespace'),
      },
      {
        regExp: setRegexLength(90),
        error: i18n.t('messages.inputMaxLength', {
          field: i18n.t('labels.bonusCode'),
          max: '90',
        }),
      },
    ],
  },
  type: {
    required: true,
    validators: [
      {
        regExp: REGEX_TEXTAREA,
        error: i18n.t('regexValidators.REGEX_TEXTAREA'),
      },
      {
        regExpMatch: REGEX_CONSECUTIVE_WHITESPACE,
        error: i18n.t('error.consecutiveWhitespace'),
      },
    ],
  },
  priority: {
    required: false,
    validators: [
      {
        regExp: REGEX_NUMBERS,
        error: i18n.t('regexValidators.REGEX_NUMBERS', {
          field: i18n.t('labels.priority'),
        }),
      },
      {
        conditional: (value: number) =>
          Number(value) >= MIN_BONUS_PRIORITY &&
          Number(value) <= MAX_BONUS_PRIORITY,
        error: i18n.t('error.betweenMinMaxErrorValue', {
          value: i18n.t('labels.priority'),
          min: MIN_BONUS_PRIORITY,
          max: MAX_BONUS_PRIORITY,
        }),
      },
    ],
  },
  ...bonusPeriodValidator,
}

export const triggerValidator = (currencies: string[]) => {
  let validator: ValidationSchema = {
    triggerBy: {
      required: true,
      validators: [
        {
          regExp: REGEX_TEXTAREA,
          error: i18n.t('regexValidators.REGEX_TEXTAREA'),
        },
        {
          regExpMatch: REGEX_CONSECUTIVE_WHITESPACE,
          error: i18n.t('error.consecutiveWhitespace'),
        },
      ],
    },
    condition: {
      required: true,
      validators: [
        {
          regExp: REGEX_TEXTAREA,
          error: i18n.t('regexValidators.REGEX_TEXTAREA'),
        },
        {
          regExpMatch: REGEX_CONSECUTIVE_WHITESPACE,
          error: i18n.t('error.consecutiveWhitespace'),
        },
      ],
      dependencie: {
        fieldName: 'triggerBy',
        conditional: (value: number) =>
          value === 1 || value === 2 || value === 5,
      },
    },
    depositCount: {
      required: true,
      validators: [
        {
          regExp: REGEX_NUMBER_FLOAT,
          error: i18n.t('regexValidators.REGEX_NUMBERS', {
            field: i18n.t('depositCount'),
          }),
        },
      ],
      dependencie: {
        fieldName: 'triggerBy',
        conditional: (value: number) =>
          value === 1 || value === 2 || value === 5,
      },
    },
    paymentMethods: {
      required: true,
      validators: [
        {
          regExp: REGEX_TEXTAREA,
          error: i18n.t('regexValidators.REGEX_TEXTAREA'),
        },
        {
          regExpMatch: REGEX_CONSECUTIVE_WHITESPACE,
          error: i18n.t('error.consecutiveWhitespace'),
        },
      ],
      dependencie: {
        fieldName: 'triggerBy',
        conditional: (value: number) => value === 1 || value === 2,
      },
    },
  }
  currencies.forEach((currency) => {
    validator = {
      ...validator,
      [`minimumDepositAmount_${currency}`]: {
        required: true,
        validators: [
          {
            conditional: (value: number) => value <= 999999999,
            error: i18n.t('error.maxErrorValue', {
              value: i18n.t('minimumAmount'),
              max: currencyFormat(999999999),
            }),
          },
          {
            regExp: REGEX_NUMBER_FLOAT,
            error: i18n.t('regexValidators.REGEX_NUMBERS', {
              field: i18n.t('minimumAmount'),
            }),
          },
        ],
        dependencie: {
          fieldName: 'triggerBy',
          conditional: (value: number) => value === 1 || value === 2,
        },
      },
    }
  })

  return validator
}

export const bonusAmountValidator = (currencies: string[]) => {
  let validator: ValidationSchema = {
    amountType: {
      required: true,
      validators: [
        {
          regExp: REGEX_TEXTAREA,
          error: i18n.t('regexValidators.REGEX_TEXTAREA'),
        },
        {
          regExpMatch: REGEX_CONSECUTIVE_WHITESPACE,
          error: i18n.t('error.consecutiveWhitespace'),
        },
      ],
    },
  }
  currencies.forEach((currency) => {
    validator = {
      ...validator,
      [`percentage_${currency}`]: {
        required: true,
        validators: [
          {
            conditional: (value: number) => value >= 0,
            error: i18n.t('error.minErrorValue', {
              value: i18n.t('percentage'),
              min: '0',
            }),
          },
        ],
        dependencie: {
          fieldName: 'amountType',
          conditional: (value: string) => value === 'PERCENTAGE',
        },
      },
      [`cap_${currency}`]: {
        required: true,
        validators: [
          {
            conditional: (value: number) => value > 0 && value <= 999999999,
            error: i18n.t('error.betweenMinMaxErrorValue', {
              value: i18n.t('cap'),
              min: currencyFormat(1),
              max: currencyFormat(999999999),
            }),
          },
        ],
        dependencie: {
          fieldName: 'amountType',
          conditional: (value: string) => value === 'PERCENTAGE',
        },
      },
      [`fixed_${currency}`]: {
        required: true,
        validators: [
          {
            conditional: (value: number) => value >= 0 && value <= 999999999,
            error: i18n.t('error.betweenMinMaxErrorValue', {
              value: i18n.t('fixed'),
              min: currencyFormat(0),
              max: currencyFormat(999999999),
            }),
          },
        ],
        dependencie: {
          fieldName: 'amountType',
          conditional: (value: string) => value === 'FIXED',
        },
      },
    }
  })
  return validator
}

export const freeSpinsValidator: ValidationSchema = {
  freeSpinQuantity: {
    required: true,
    validators: [
      {
        regExp: REGEX_NUMBERS,
        error: i18n.t('regexValidators.REGEX_NUMBERS', {
          field: i18n.t('labels.freeSpinQuantity'),
        }),
      },
      {
        conditional: (value: number) =>
          value >= MIN_FREE_SPIN_QUANTITY && value <= MAX_FREE_SPIN_QUANTITY,
        error: i18n.t('error.betweenMinMaxErrorValue', {
          value: i18n.t('labels.freeSpinQuantity'),
          min: MIN_FREE_SPIN_QUANTITY,
          max: MAX_FREE_SPIN_QUANTITY,
        }),
      },
    ],
  },
}

export const rolloverSettingsValidator = (currencies: string[]) => {
  let validator: ValidationSchema = {
    mode: {
      required: true,
      validators: [
        {
          regExp: REGEX_TEXTAREA,
          error: i18n.t('regexValidators.REGEX_TEXTAREA'),
        },
        {
          regExpMatch: REGEX_CONSECUTIVE_WHITESPACE,
          error: i18n.t('error.consecutiveWhitespace'),
        },
      ],
    },
    rolloverCoefficcient: {
      required: true,
      validators: [
        {
          regExp: REGEX_TEXTAREA,
          error: i18n.t('regexValidators.REGEX_TEXTAREA'),
        },
        {
          regExpMatch: REGEX_CONSECUTIVE_WHITESPACE,
          error: i18n.t('error.consecutiveWhitespace'),
        },
      ],
    },
    rollover: {
      required: true,
      validators: [
        {
          regExp: REGEX_NUMBERS,
          error: i18n.t('regexValidators.REGEX_NUMBERS', {
            field: i18n.t('labels.rolloverCoefficient'),
          }),
        },
        {
          conditional: (value: number) => value > 0,
          error: i18n.t('error.minErrorValue', {
            value: i18n.t('labels.rolloverCoefficient'),
            min: 1,
          }),
        },
      ],
    },
    order: {
      required: true,
      validators: [],
      dependencie: {
        fieldName: 'mode',
        conditional: (value: string) => value === BonusModeType.NON_STICKY,
      },
    },
  }

  currencies.forEach((currency) => {
    validator = {
      ...validator,
      [`rmMinAmount_${currency}`]: {
        required: true,
        validators: [
          {
            conditional: (value: number) => value <= 999999999,
            error: i18n.t('error.maxErrorValue', {
              value: i18n.t('rmMinAmount'),
              max: currencyFormat(999999999),
            }),
          },
          {
            regExp: REGEX_NUMBER_FLOAT,
            error: i18n.t('regexValidators.REGEX_NUMBERS', {
              field: i18n.t('rmMinAmount'),
            }),
          },
        ],
        dependencie: {
          fieldName: 'order',
          conditional: (value: string) =>
            value === BonusWageringOrderType.RM_FIRST,
        },
      },
    }
  })

  return validator
}

export const rolloverRestrictionsValidator = (currencies: string[]) => {
  let validator: ValidationSchema = {
    products: {
      required: false,
      validators: [
        {
          regExp: REGEX_TEXTAREA,
          error: i18n.t('regexValidators.REGEX_TEXTAREA'),
        },
      ],
    },
    sports: {
      required: false,
      validators: [
        {
          regExp: REGEX_TEXTAREA,
          error: i18n.t('regexValidators.REGEX_TEXTAREA'),
        },
      ],
    },
    wagerTypes: {
      required: false,
      validators: [
        {
          regExp: REGEX_TEXTAREA,
          error: i18n.t('regexValidators.REGEX_TEXTAREA'),
        },
      ],
    },
    maxLine: {
      required: false,
      validators: [
        {
          regExp: REGEX_TEXTAREA,
          error: i18n.t('regexValidators.REGEX_TEXTAREA'),
        },
        {
          conditional: (value: number) => value <= -101 && value >= -999999999,
          error: i18n.t('error.betweenMinMaxErrorValue', {
            value: i18n.t('restrictions.maxLine'),
            min: currencyFormat(-101),
            max: currencyFormat(-999999999),
          }),
        },
      ],
    },
    minLine: {
      required: false,
      validators: [
        {
          regExp: REGEX_TEXTAREA,
          error: i18n.t('regexValidators.REGEX_TEXTAREA'),
        },
        {
          conditional: (value: number) => value >= 101 && value <= 999999999,
          error: i18n.t('error.betweenMinMaxErrorValue', {
            value: i18n.t('restrictions.minLine'),
            min: currencyFormat(101),
            max: currencyFormat(999999999),
          }),
        },
      ],
    },
    rolloverAmountCalculationType: {
      required: false,
      validators: [],
    },
  }
  currencies.forEach((currency) => {
    validator = {
      ...validator,
      [`minResetRealMoneyAmount_${currency}`]: {
        required: false,
        validators: [
          {
            conditional: (value: number) => value <= 999999999,
            error: i18n.t('error.maxErrorValue', {
              value: i18n.t('minResetRealMoneyAmount'),
              max: currencyFormat(999999999),
            }),
          },
          {
            regExp: REGEX_NUMBER_FLOAT,
            error: i18n.t('regexValidators.REGEX_NUMBERS', {
              field: i18n.t('minResetRealMoneyAmount'),
            }),
          },
        ],
      },
    }
  })
  return validator
}

export const restrictionsValidator = (
  currencies: string[],
  bonusType: string,
) => {
  let validator: ValidationSchema = {
    providerCategory: {
      required: false,
      validators: [
        {
          regExp: REGEX_TEXTAREA,
          error: i18n.t('regexValidators.REGEX_TEXTAREA'),
        },
      ],
    },
    providers: {
      required:
        bonusType === BonusType.BETSOFT_CASINO ||
        bonusType === BonusType.CASINO_STANDARD ||
        bonusType === BonusType.FREE_SPINS,
      validators:
        bonusType === BonusType.BETSOFT_CASINO ||
        bonusType === BonusType.CASINO_STANDARD ||
        bonusType === BonusType.FREE_SPINS
          ? [
              {
                conditional: (value: string[]) => value.length > 0,
                error: i18n.t('error.requiredFieldArray', {
                  min: 1,
                  field: 'provider',
                }),
              },
            ]
          : [],
    },
    games: {
      required: bonusType === BonusType.FREE_SPINS,
      validators:
        bonusType === BonusType.FREE_SPINS
          ? [
              {
                conditional: (value: string[]) => value.length > 0,
                error: i18n.t('error.requiredFieldArray', {
                  min: 1,
                  field: 'game',
                }),
              },
              {
                conditional: (value: string[]) =>
                  value.length <= MAX_FREE_SPIN_GAMES,
                error: i18n.t('error.maxErrorValue', {
                  value: i18n.t('labels.games'),
                  max: MAX_FREE_SPIN_GAMES,
                }),
              },
            ]
          : [],
    },
    products: {
      required: false,
      validators: [
        {
          regExp: REGEX_TEXTAREA,
          error: i18n.t('regexValidators.REGEX_TEXTAREA'),
        },
      ],
    },
    sports: {
      required: false,
      validators: [
        {
          regExp: REGEX_TEXTAREA,
          error: i18n.t('regexValidators.REGEX_TEXTAREA'),
        },
      ],
    },
    wagerTypes: {
      required: false,
      validators: [
        {
          regExp: REGEX_TEXTAREA,
          error: i18n.t('regexValidators.REGEX_TEXTAREA'),
        },
      ],
    },
    maxLine: {
      required: false,
      validators: [
        {
          regExp: REGEX_TEXTAREA,
          error: i18n.t('regexValidators.REGEX_TEXTAREA'),
        },
        {
          conditional: (value: number) => value <= -101 && value >= -999999999,
          error: i18n.t('error.betweenMinMaxErrorValue', {
            value: i18n.t('restrictions.maxLine'),
            min: currencyFormat(-101),
            max: currencyFormat(-999999999),
          }),
        },
      ],
    },
    minLine: {
      required: false,
      validators: [
        {
          regExp: REGEX_TEXTAREA,
          error: i18n.t('regexValidators.REGEX_TEXTAREA'),
        },
        {
          conditional: (value: number) => value >= 101 && value <= 999999999,
          error: i18n.t('error.betweenMinMaxErrorValue', {
            value: i18n.t('restrictions.minLine'),
            min: currencyFormat(101),
            max: currencyFormat(999999999),
          }),
        },
      ],
    },
    expirationType: {
      required: false,
      validators: [],
    },
    expirationUnit: {
      // BGaming needs a valid_until, so free spins cannot be issued without an expiration
      required: bonusType === BonusType.FREE_SPINS,
      validators: [
        {
          regExp: REGEX_NUMBERS,
          error: i18n.t('messages.formatError', {
            field: i18n.t('labels.expirationTime'),
          }),
        },
        {
          conditional: (value: string) => {
            return Number(value) > 0
          },
          error: i18n.t('messages.formatError', {
            field: i18n.t('labels.expirationTime'),
          }),
        },
      ],
      dependencie: {
        fieldName: 'expirationType',
        conditional: (value: string) => value === ExpirationType.TIME_UNIT,
      },
    },
    expirationDate: {
      required: bonusType === BonusType.FREE_SPINS,
      validators: [],
      dependencie: {
        fieldName: 'expirationType',
        conditional: (value: string) => value === ExpirationType.SPECIFIC_DATE,
      },
    },
    maxBonusesAllowed: {
      required: false,
      validators: [
        {
          regExp: REGEX_NUMBERS,
          error: i18n.t('messages.formatError', {
            field: i18n.t('labels.maxBonusesAllowed'),
          }),
        },
        {
          conditional: (value: string) => {
            return Number(value) > 0
          },
          error: i18n.t('messages.formatError', {
            field: i18n.t('labels.maxBonusesAllowed'),
          }),
        },
      ],
    },
  }

  currencies.forEach((currency) => {
    validator = {
      ...validator,
      [`maxBet_${currency}`]: {
        required: false,
        validators: [
          {
            conditional: (value: number) => value > 0 && value <= 999999999,
            error: i18n.t('error.betweenMinMaxErrorValue', {
              value: i18n.t('bonusMaxBet'),
              min: currencyFormat(1),
              max: currencyFormat(999999999),
            }),
          },
          {
            regExp: REGEX_NUMBERS,
            error: i18n.t('regexValidators.REGEX_NUMBERS', {
              field: i18n.t('bonusMaxBet'),
            }),
          },
        ],
      },
      [`maxWin_${currency}`]: {
        required: false,
        validators: [
          {
            regExp: REGEX_NUMBERS,
            error: i18n.t('regexValidators.REGEX_NUMBERS', {
              field: i18n.t('bonusMaxWin'),
            }),
          },
          {
            conditional: (value: number) => value > 0 && value <= 999999999,
            error: i18n.t('error.betweenMinMaxErrorValue', {
              value: i18n.t('bonusMaxWin'),
              min: currencyFormat(1),
              max: currencyFormat(999999999),
            }),
          },
        ],
      },
    }
  })
  return validator
}

export const eligibilityValidator: ValidationSchema = {
  promotionalCodesAvailables: {
    required: false,
    validators: [],
  },
  promotionalCodesUnavailables: {
    required: false,
    validators: [],
  },
  creditCardTiersAvailables: {
    required: false,
    validators: [],
  },
  creditCardTiersUnavailables: {
    required: false,
    validators: [],
  },
  isEnable: {
    required: false,
    validators: [],
  },
  agents: {
    required: false,
    validators: [],
  },
  includeCountry: {
    required: false,
    validators: [],
  },
  countries: {
    required: false,
    validators: [],
  },
}

export const dependenciesValidator: ValidationSchema = {
  bonusBundle: {
    required: false,
    validators: [],
  },
}

export const enableValidator: ValidationSchema = {
  isActive: {
    required: false,
    validators: [],
  },
  // isMakeUpRule: {
  //   required: false,
  //   validators: [],
  // },
}

export default bonusDetailsValidator
